<template>
  <v-card flat outlined :loading="isMainLoading || isLoading || isSaveing" min-height="30">
    <template v-if="!isLoading && penaltySetting">
      <v-form @submit.prevent="saveMarketBill" ref="form" :lazy-validation="true">
        <v-card elevation="0">
          <v-card-title>{{ $t('penalty.penalty_out_of_date') }}</v-card-title>
          <v-card-text>
            <div class="flex due-date-box">
              <div class="text">{{ $t('penalty.penalty_type') }}</div>
              <div style="width:400px;">
                <v-select
                  outlined
                  :label="$t('penalty.penalty_type')"
                  :placeholder="$t('penalty.penalty_type')"
                  v-model="penalty.penaltyType"
                  :items="penaltyItems"
                  @change="$refs.form.resetValidation()"
                ></v-select>
              </div>
            </div>
            <div v-if="penalty.penaltyType" class="px-10">
              <div class="flex due-date-box">
                <div class="text-p">{{ $t('penalty.select_penalty_list') }}</div>
                <div>
                  <v-select
                    outlined
                    :label="$t('penalty.select_penalty_type')"
                    :placeholder="$t('penalty.select_penalty_type')"
                    v-model="penalty.penaltyProductId"
                    :rules="[rules.required]"
                    :disabled="isLoading"
                    :items="accountingProducts"
                    :item-text="(r) => `[${r.typeString}] ${r.name} @${r.unit}`"
                    :item-value="(r) => r.objectId"
                    :no-data-text="$t('penalty.no_penalty_list')"
                  ></v-select>
                </div>
              </div>
              <div class="flex due-date-box" v-if="penalty.penaltyType == 'cost'">
                <div class="text-p">{{ $t('penalty.penalty_per_day') }}</div>
                <div style="min-width:340px;">
                  <v-currency-field
                    class="input-right"
                    :label="`${$t('penalty.penalty_per_day_amount')}` "
                    :placeholder="$t('penalty.penalty_per_day_amount')"
                    v-model="penalty.penaltyCost"
                    :rules="[rules.minCost]"
                    outlined
                    :disabled="!penalty.penaltyProductId"
                  />
                </div>
                <div>{{ $helper.currencyThEn(tabType) }}</div>
              </div>
              <div class="flex due-date-box" v-else-if="penalty.penaltyType == 'percent'">
                <div class="text-p">{{ $t('penalty.penalty_per_day') }} ({{'%'}})</div>
                <div style="min-width:340px;">
                  <v-currency-field
                    class="input-right"
                    :label="`${$t('penalty.penalty_per_day_percent')} (%)` "
                    :rules="[rules.minPercent ]"
                    :placeholder="$t('penalty.penalty_per_day_percent')"
                    v-model="penalty.penaltyPercent"
                    outlined
                    :disabled="!penalty.penaltyProductId"
                  />
                </div>
                <div>%</div>
              </div>
              <div class="due-date-box" v-else-if="penalty.penaltyType == 'step'">
                <div class="text-p">
                  {{ $t('penalty.step_penalty') }} (
                  <v-icon>mdi mdi-stairs</v-icon>)
                </div>
                <div
                  v-if="penalty.penaltyStep && penalty.penaltyStep.length > 0"
                  class="penalty-step-box"
                >
                  <div
                    class="flex"
                    style="margin:0;"
                    v-for="(penaltyStep,index) of penalty.penaltyStep"
                    :key="index"
                  >
                    <div class="flex pt-5 left-box" v-if="index != penalty.penaltyStep.length-1">
                      <div style="min-width:140px;" class="text-right">
                        <b>{{ $t('penalty.over_due_since') }}</b>
                      </div>
                      <v-currency-field
                        class="input-right"
                        style="width:150px;"
                        :label="$t('penalty.amount_days')"
                        :rules="[rules.minDays]"
                        v-model="penaltyStep.minDays"
                        outlined
                        :disabled="!penalty.penaltyProductId || index == 0 || true"
                        hide-details
                      />
                      <div class="text-left">{{ $t('day') }}</div>
                      <div>
                        <b>{{ $t('to') }}</b>
                      </div>
                      <v-currency-field
                        class="input-right"
                        style="width:190px;"
                        :label="$t('penalty.amount_days')"
                        :rules="[penaltyStep.minDays > penaltyStep.maxDays ? rules.maxDays : true]"
                        :placeholder="$t('penalty.amount_days')"
                        v-model="penaltyStep.maxDays"
                        outlined
                        :disabled="!penalty.penaltyProductId"
                        :hide-details="penaltyStep.minDays > penaltyStep.maxDays ? false : true"
                        @input="checkInputStep(index)"
                        @keypress="preventDecimalInput"
                        @paste="ignoreCopyValueDecimal"
                      />
                      <div class="text-left">{{ $t('day') }}</div>
                    </div>
                    <div class="flex left-box" v-else style="justify-content:center;">
                      <div>
                        <b>
                          {{ $t('penalty.over_due_since') }}
                          <u>{{penaltyStep.moreDays}}</u> {{ $t('penalty.more_days') }}
                        </b>
                      </div>
                    </div>
                    <div class="flex  right-box pt-5 pr-0">
                      <div style="min-width:90px;" class="text-right">
                        <b>{{ $t('penalty.calculate_penalty') }}</b>
                      </div>
                      <v-select
                        style="width:150px;"
                        outlined
                        :label="$t('penalty.calculate_penalty_type')"
                        v-model="penaltyStep.isPerDay"
                        :disabled="!penalty.penaltyProductId"
                        :items="[{value:true,text:$t('penalty.per_day')},{value:false,text:$t('penalty.flat_rate')}]"
                        hide-details
                      ></v-select>
                      <v-currency-field
                        style="width:200px;"
                        class="input-right"
                        :label="`${$t('penalty.penalty_amount_label', { perType: penaltyStep.isPerDay ? $t('penalty.per_day') : $t('penalty.flat_rate')})}`"
                        :rules="[rules.minCostStep, rules.isEmpty]"
                        :placeholder="`${$t('penalty.penalty_amount_label', { perType: penaltyStep.isPerDay ? $t('penalty.per_day') : $t('penalty.flat_rate')})}`"
                        v-model="penaltyStep.penaltyCost"
                        outlined
                        :disabled="!penalty.penaltyProductId"
                        hide-details
                        @keypress="preventDecimalInput"
                        @paste="ignoreCopyValueDecimal"
                      />
                      <div style="min-width:40px;" class="text-center">
                       {{ $helper.currencyThEn(tabType) }}
                      </div>
                      <v-tooltip right>
                        <template v-slot:activator="{ on, attrs }">
                          <div v-bind="attrs" v-on="on" style="padding:0;">
                            <v-icon
                              :disabled="!penalty.penaltyProductId"
                              :color="checkRowStep(penaltyStep,index == penalty.penaltyStep.length-1).isError 
                                ? 'error' 
                                : checkRowStep(penaltyStep,index == penalty.penaltyStep.length-1).isWarning 
                                  ? 'warning' 
                                  : 'success'"
                            >mdi mdi-alert-circle</v-icon>
                          </div>
                        </template>
                        <div
                          class="tooltip-box"
                          v-if="!checkRowStep(penaltyStep,index == penalty.penaltyStep.length-1).isError && !checkRowStep(penaltyStep,index == penalty.penaltyStep.length-1).isWarning"
                        >
                          <div>{{checkRowStep(penaltyStep,index == penalty.penaltyStep.length-1).textRes}}</div>
                          <div
                            class="pl-2"
                          >- {{checkRowStep(penaltyStep,index == penalty.penaltyStep.length-1).textMinCost}}</div>
                          <div
                            class="pl-2"
                          >- {{checkRowStep(penaltyStep,index == penalty.penaltyStep.length-1).textMaxCost}}</div>
                        </div>
                        <div class="tooltip-box" v-else>
                          <div v-if="parseFloat(penaltyStep.penaltyCost) !== 0">{{ $t('validate.please_input_correct_data') }}</div>
                          <div
                            v-for="(textError,index) of checkRowStep(penaltyStep,index == penalty.penaltyStep.length-1).textError"
                            :key="index"
                          >
                            <span v-if="Object.is(parseFloat(penaltyStep.penaltyCost), 0) || Object.is(parseFloat(penaltyStep.penaltyCost), -0)">{{ textError }}</span>
                            <span v-else class="pl-2">- {{textError}}</span>
                          </div>
                        </div>
                      </v-tooltip>
                    </div>
                  </div>
                  <div class="w-auto text-right my-4">
                    <v-btn
                      @click="addPenaltyStep()"
                      class="mx-2"
                      color="success"
                      :disabled="!penalty.penaltyProductId"
                    >
                      <v-icon left color="white">mdi-plus</v-icon>{{ $t('action_type.add') }}
                    </v-btn>
                    <v-btn
                      @click="delPenaltyStep()"
                      class="mx-2"
                      color="error"
                      :disabled="!penalty.penaltyProductId"
                    >
                      <v-icon left color="white">mdi-minus</v-icon>{{ $t('action_type.delete') }}
                    </v-btn>
                  </div>
                </div>
                <div v-else>{{ $t('noinfo') }}</div>
              </div>
              <div class="due-date-box" v-else-if="penalty.penaltyType == 'step-unpaid'">
                <div class="text-p">
                  {{ $t('penalty.penalty_step_unpaid') }} (
                  <v-icon>mdi mdi-stairs</v-icon>)
                </div>
                <div
                  v-if="penalty.penaltyStepUnpaid && penalty.penaltyStepUnpaid.length > 0"
                  class="penalty-step-box"
                >
                  <div
                    class="flex"
                    style="margin:0;"
                    v-for="(penaltyStep,index) of penalty.penaltyStepUnpaid"
                    :key="index"
                  >
                    <div class="flex pt-5 left-box" v-if="index != penalty.penaltyStepUnpaid.length-1">
                      <div style="min-width:150px;" class="text-right">
                        <b>{{ $t('penalty.overdue_amount_more_than') }}</b>
                      </div>
                      <v-currency-field
                        class="input-right"
                        style="width:150px;"
                        :label="$helper.currencyThEn(tabType)"
                        :value="penaltyStep.min - 1"
                        outlined
                        :disabled="!penalty.penaltyProductId || index == 0 || true"
                        hide-details
                      />
                      <v-currency-field
                        class="input-right d-none"
                        style="width:150px;"
                        :label="$helper.currencyThEn(tabType)"
                        :rules="[rules.minDays]"
                        v-model="penaltyStep.min"
                        outlined
                        :disabled="!penalty.penaltyProductId || index == 0 || true"
                        hide-details
                      />
                      <div class="text-left">{{ $helper.currencyThEn(tabType) }}</div>
                      <div>
                        <b>{{ $t('to') }}</b>
                      </div>
                      <v-currency-field
                        class="input-right"
                        style="width:190px;"
                        :label="$helper.currencyThEn(tabType)"
                        :rules="[penaltyStep.min > penaltyStep.max ? rules.maxDays : true]"
                        :placeholder="$helper.currencyThEn(tabType)"
                        v-model.number="penaltyStep.max"
                        outlined
                        :disabled="!penalty.penaltyProductId"
                        :hide-details="penaltyStep.min > penaltyStep.max ? false : true"
                        @input="checkInputStepUnpaid(index)"
                        step="1"
                        @keypress="preventDecimalInput"
                        @paste="ignoreCopyValueDecimal"
                      />
                      <div class="text-left">{{ $helper.currencyThEn(tabType) }}</div>
                    </div>
                    <div class="flex left-box" v-else style="justify-content:center;">
                      <div>
                        <b>
                          {{$t('penalty.overdue_amount_more_than')}}
                          <u>{{penaltyStep.more - 1}}</u> {{ $helper.currencyThEn(tabType) }}{{ $t('validate.up_to') }}
                        </b>
                      </div>
                    </div>
                    <div class="flex  right-box pt-5 pr-0">
                      <div style="min-width:90px;" class="text-right">
                        <b>{{ $t('penalty.calculate_penalty') }}</b>
                      </div>
                      <v-select
                        style="width:150px;"
                        outlined
                        :label="$t('penalty.calculate_penalty_type')"
                        v-model="penaltyStep.isPerDay"
                        :disabled="!penalty.penaltyProductId"
                        :items="[{value:true,text:$t('penalty.per_day')},{value:false,text:$t('penalty.flat_rate')}]"
                        hide-details
                      ></v-select>
                      <v-currency-field
                        style="width:200px;"
                        class="input-right"
                        :label="`${$t('penalty.penalty_amount_label', { perType: penaltyStep.isPerDay ? $t('penalty.per_day') : $t('penalty.flat_rate')})}`"
                        :rules="[rules.minCostStep, rules.isEmpty]"
                        :placeholder="`${$t('penalty.penalty_amount_label', { perType: penaltyStep.isPerDay ? $t('penalty.per_day') : $t('penalty.flat_rate')})}`"
                        v-model="penaltyStep.penaltyCost"
                        outlined
                        :disabled="!penalty.penaltyProductId"
                        hide-details
                        @keypress="preventDecimalInput"
                        @paste="ignoreCopyValueDecimal"
                      />
                      <div style="min-width:40px;" class="text-center">
                       {{ $helper.currencyThEn(tabType) }}
                      </div>
                      <v-tooltip right>
                        <template v-slot:activator="{ on, attrs }">
                          <div v-bind="attrs" v-on="on" style="padding:0;">
                            <v-icon
                              :disabled="!penalty.penaltyProductId"
                              :color="checkRowStepUnpaid(penaltyStep, index == penalty.penaltyStepUnpaid.length - 1).isError 
                                ? 'error' 
                                : checkRowStepUnpaid(penaltyStep, index == penalty.penaltyStepUnpaid.length - 1).isWarning 
                                  ? 'warning' 
                                  : 'success'"
                            >mdi mdi-alert-circle</v-icon>
                          </div>
                        </template>
                        <div
                          class="tooltip-box"
                          v-if="!checkRowStepUnpaid(penaltyStep,index == penalty.penaltyStepUnpaid.length-1).isError && !checkRowStepUnpaid(penaltyStep,index == penalty.penaltyStepUnpaid.length-1).isWarning"
                        >
                          <div>{{checkRowStepUnpaid(penaltyStep,index == penalty.penaltyStepUnpaid.length-1).textRes}}</div>
                          <div
                            class="pl-2"
                          >- {{checkRowStepUnpaid(penaltyStep,index == penalty.penaltyStepUnpaid.length-1).textMinCost}}</div>
                          <div
                            class="pl-2"
                          >- {{checkRowStepUnpaid(penaltyStep,index == penalty.penaltyStepUnpaid.length-1).textMaxCost}}</div>
                        </div>
                        <div class="tooltip-box" v-else>
                          <div v-if="parseFloat(penaltyStep.penaltyCost) !== 0">{{ $t('validate.please_input_correct_data') }}</div>
                          <div
                            v-for="(textError,index) of checkRowStepUnpaid(penaltyStep,index == penalty.penaltyStepUnpaid.length-1).textError"
                            :key="index"
                          >
                            <span v-if="Object.is(parseFloat(penaltyStep.penaltyCost), 0) || Object.is(parseFloat(penaltyStep.penaltyCost), -0)">{{ textError }}</span>
                            <span v-else class="pl-2">- {{textError}}</span>
                          </div>
                        </div>
                      </v-tooltip>
                    </div>
                  </div>
                  <div class="w-auto text-right my-4">
                    <v-btn
                      @click="addPenaltyStepUnpaid()"
                      class="mx-2"
                      color="success"
                      :disabled="!penalty.penaltyProductId"
                    >
                      <v-icon left color="white">mdi-plus</v-icon>{{ $t('action_type.add') }}
                    </v-btn>
                    <v-btn
                      @click="delPenaltyStepUnpaid()"
                      class="mx-2"
                      color="error"
                      :disabled="!penalty.penaltyProductId"
                    >
                      <v-icon left color="white">mdi-minus</v-icon>{{ $t('action_type.delete') }}
                    </v-btn>
                  </div>
                </div>
                <div v-else>{{ $t('noinfo') }}</div>
              </div>
              <div class="flex due-date-box" v-if="false">
                <div class="text-p">{{ $t('penalty.minimum_penalty') }}</div>
                <div>
                  <v-currency-field
                    class="input-right"
                    :label="`${$t('penalty.minimum_penalty')}`"
                    :placeholder="`${$t('penalty.minimum_penalty_amount')}`"
                    v-model="penalty.penaltyMin"
                    outlined
                    :disabled="!penalty.penaltyProductId"
                    @keypress="preventDecimalInput"
                    @paste="ignoreCopyValueDecimal"
                  />
                </div>
              </div>
              <div class="flex due-date-box">
                <div class="text-p">{{ $t('penalty.maximum_penalty') }}</div>
                <div>
                  <v-currency-field
                    class="input-right"
                    :label="`${$t('penalty.maximum_penalty')}`"
                    :placeholder="`${$t('penalty.maximum_penalty_amount')}`"
                    v-model="penalty.penaltyMax"
                    outlined
                    :disabled="!penalty.penaltyProductId"
                    :rules="[rules.maxPenaltyCost]"
                    @keypress="preventDecimalInput"
                    @paste="ignoreCopyValueDecimal"
                  />
                </div>
                <div>{{ $helper.currencyThEn(tabType) }}</div>
              </div>
            </div>
          </v-card-text>
          <v-card-actions class="flex sticky-bottom">
            <v-btn
              large
              :disabled="isLoading || isSaveing ||
                penalty.penaltyType != '' && (
                  !penalty.penaltyProductId || 
                  (
                    penalty.penaltyType == 'cost' && 
                ( !penalty.penaltyCost || 
                  penalty.penaltyCost <= 0)) || 
                  (
                    penalty.penaltyType == 'percent' &&
                  (!penalty.penaltyPercent || 
                  penalty.penaltyPercent <= 0)
                  ))
                  "
              :loading="isSaveing"
              color="success"
              type="submit"
            >{{ $t('save') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </template>
    <div v-if="!isLoading && !penaltySetting" class="text-center p-10">{{ $t('market.no_market_data') }}</div>
  </v-card>
</template>

<script>
import { mapGetters , mapActions} from "vuex";
import { Market, Admin, Employee } from "@/classes";
export default {
  props: {
    isMainLoading: Boolean,
    tabType: {
      type: String,
      default: function() {
        return this.$globalCurrencyCode
      }
    },
    reload: {
      type: Boolean,
      default: false
    }
  },
  components: {},
  computed: {
    ...mapGetters({
      accountingProducts: "accountingProducts/getAccountingProductPenalty"
    }),

    employeeItems() {}
  },
  data() {
    return {
      projectId: this.$route.params.projectId,
      marketId: this.$route.params.marketId,
      market: null,
      penaltySetting: null,
      isSaveing: false,
      isLoading: false,
      projectAdmins: [],
      projectEmployee: [],
      employees: [],
      imageUpLoading: false,
      imageLoading: false,
      imgUpload: null,
      startDate: this.$moment().format("DD/MM/YYYY"),
      penaltyItems: [
        { value: "", text: this.$t('market.no_penalty') },
        { value: "cost", text: `${this.$t('market.constant_penalty')}` },
        { value: "percent", text: this.$t('market.percent_penalty') },
        { value: "step", text: `${this.$t('penalty.step_penalty')} (${this.$t('day')})` },
        { value: "step-unpaid", text: `${this.$t('penalty.step_penalty')} (${this.$t('invoice.remaining_amount')})` }
      ],
      creditTerm: null,
      penalty: {
        penaltyProductId: undefined,
        penaltyType: "",
        penaltyCost: undefined,
        penaltyPercent: undefined,
        penaltyMax: undefined,
        penaltyMin: undefined,
        penaltyStep: undefined,
        penaltyStepUnpaid: undefined,
        isRound: false
      },
      rules: {
        required: value => !!value || this.$t('validate.please_select_list'),
        maxPercent: v => v <= 100 || this.$t('validate.please_max_percent'),
        minPercent: v => v > 0 || this.$t('validate.please_min_percent'),
        minDays: v => v > 0 || this.$t('validate.please_min_cost', { currencySign: '' }),
        maxDays: v => this.$t('validate.please_more_or_equal_days'),
        minCost: v => v > 0 || `${this.$t('validate.please_min_cost', { currencySign: this.$helper.currencySign(this.tabType) })}`,
        minCostStep: v => (v >= 0 && !Object.is(v, -0)) || `${this.$t('validate.please_input_more_than_zero', { currencySign: this.$helper.currencySign(this.tabType) })}`,
        maxPenaltyCost: v => v === '' || v === null || v === undefined || v >= 0 || `${this.$t('validate.please_input_more_than_zero', { currencySign: this.$helper.currencySign(this.tabType) })}`,
        isEmpty: v => (v !== '' && v !== null && v !== undefined) || v === 0 || this.$t('validate.please_specify_data'),
      },
    };
  },
  watch: {
    reload: {
      handler(newVal) {
        if (newVal) {
          this.initData();
          this.getAccountingProducts();
        }
      },
      immediate: false
    },
  },
  mounted() {
    this.initData();
    this.getAccountingProducts();
  },
  methods: {
    ...mapActions({
      getPenaltySettingByMarket: "penaltySetting/getPenaltySettingByMarket",
      createPenaltySetting: "penaltySetting/createPenaltySetting",
      updatePenaltySetting: "penaltySetting/updatePenaltySetting"
    }),
    preventDecimalInput(event) {
      if (event.key === '.') {
        event.preventDefault();
      }
    },
    ignoreCopyValueDecimal(event){
      const pastedText = event.clipboardData.getData('text');
      if (pastedText.includes('.')) {
        event.preventDefault();
      }
    },
    async initData() {
      this.penaltySetting = null;
      this.isLoading = true;
      try {
        this.resetForm();
        await Promise.all([
          this.initAdmin(),
          this.initEmployee()
        ]);
        this.penaltySetting = await this.getPenaltySettingByMarket({
          marketId: this.marketId,
          currencyCode: this.tabType
        });
        if (this.penaltySetting) {
          this.initPenalty(this.penaltySetting);
        }
        this.isLoading = false;
      } catch (error) {
        console.log(error);
        this.$toast.show({
          content: `${this.$t('error_and_message', { error: error.message })}`,
          color: "error"
        });
        this.isLoading = false;
      }
    },
    initPenalty(penalty) {
      let {
        penaltyProductId,
        penaltyType,
        penaltyCost,
        penaltyPercent,
        penaltyMax,
        penaltyMin,
        penaltyStep,
        penaltyStepUnpaid,
        isRound
      } = penalty;
      this.penalty.isRound = isRound || false;
      this.penalty.penaltyProductId = penaltyProductId;
      this.penalty.penaltyType = penaltyType || "";
      this.penalty.penaltyCost = penaltyCost ? parseFloat(penaltyCost) : 0;
      this.penalty.penaltyPercent = penaltyPercent ? parseFloat(penaltyPercent) : 0;
      this.penalty.penaltyMax = penaltyMax && parseFloat(penaltyMax) > 0 ? parseFloat(penaltyMax) : undefined;
      this.penalty.penaltyMin = penaltyMin ? parseFloat(penaltyMin) : 0;
      if (penaltyStep) {
        this.penalty.penaltyStep = penaltyStep;
      }
      if (penaltyStepUnpaid) {
        this.penalty.penaltyStepUnpaid = penaltyStepUnpaid;
      }
    },
    resetForm() {
      this.penalty.penaltyProductId = undefined;
      this.penalty.penaltyType = "";
      this.penalty.penaltyCost = undefined;
      this.penalty.penaltyPercent = undefined;
      this.penalty.penaltyMax = undefined;
      this.penalty.penaltyMin = undefined;
      this.penalty.penaltyStep = [
        {
          minDays: 1,
          maxDays: null,
          moreDays: 0,
          penaltyCost: 0,
          isPerDay: true
        },
        {
          minDays: 1,
          maxDays: null,
          moreDays: 0,
          penaltyCost: 0,
          isPerDay: true
        }
      ];
      this.penalty.penaltyStepUnpaid = [
        {
          min: 1,
          max: null,
          more: 0,
          penaltyCost: 0,
          isPerDay: true
        },
        {
          min: 1,
          max: null,
          more: 0,
          penaltyCost: 0,
          isPerDay: true
        }
      ];
      this.penalty.isRound = false;
      this.penalty.currencyCode = this.tabType;
    },
    async initAdmin() {
      this.projectAdmins = await Admin.getAdminByProject(this.projectId);
      for (const userA of this.projectAdmins) {
        userA.user.fullName = `${userA.user.firstName} ${userA.user.lastName}`;
        let userT = this.employees.find(i => i.objectId == userA.user.objectId);
        if (!userT) this.employees.push(userA.user);
      }
    },
    async initEmployee() {
      this.projectEmployee = await Employee.getEmployeeByProject(
        this.projectId
      );
      for (const userA of this.projectEmployee) {
        userA.user.fullName = `${userA.user.firstName} ${userA.user.lastName}`;
        let userT = this.employees.find(i => i.objectId == userA.user.objectId);
        if (!userT) this.employees.push(userA.user);
      }
    },
    async getAccountingProducts() {
      await this.$store.dispatch(
        "accountingProducts/getAccountingProducts",
        this.projectId
      );
    },
    async saveMarketBill() {
      try {
        if(this.$refs.form.validate()){
          this.isSaveing = true;
          if (this.penalty) {
            this.penalty.penaltyCost = this.penalty.penaltyCost ? parseFloat(this.penalty.penaltyCost) : 0;
            this.penalty.penaltyPercent = this.penalty.penaltyPercent ? parseFloat(this.penalty.penaltyPercent) : 0;
            this.penalty.penaltyMax = this.penalty.penaltyMax ? parseFloat(this.penalty.penaltyMax) : null;
            this.penalty.penaltyMin = this.penalty.penaltyMin ? parseFloat(this.penalty.penaltyMin) : 0;
            if(this.penaltySetting && this.penaltySetting.objectId){
              this.penaltySetting = await this.updatePenaltySetting({
                id: this.penaltySetting.objectId,
                ...this.penalty,
                currencyCode: this.tabType
              });
            }else{
              this.penaltySetting = await this.createPenaltySetting({
                marketId: this.marketId,
                currencyCode: this.tabType,
                ...this.penalty
              });
            }
            this.$toast.show({
              content: `${this.$t('save_success')}`,
              color: "success"
            });
          } else {
            this.$toast.show({
              content: `${this.$t('error')} : ${this.$t('penalty.no_penalty_info')}`,
              color: "error"
            });
          }
          this.isSaveing = false;
        }
      } catch (error) {
        this.$toast.show({
          content: `${this.$t('error_and_message', { error: error.message })}`,
          color: "error"
        });
        this.isSaveing = false;
      }
    },
    addPenaltyStep() {
      if (this.penalty && this.penalty.penaltyStep && this.penalty.penaltyStep.length >= 2) {
        let penaltyStepLength = this.penalty.penaltyStep.length;
        let bfPenaltyStep = this.penalty.penaltyStep[penaltyStepLength - 2];
        let lastPenaltyStep = this.penalty.penaltyStep[penaltyStepLength - 1];
        let lastPenaltyCost = 0;
        lastPenaltyStep.minDays = bfPenaltyStep ? parseFloat(bfPenaltyStep.maxDays) + 1 : 1;
        lastPenaltyStep.maxDays = bfPenaltyStep ? parseFloat(bfPenaltyStep.maxDays) + 1 : 0;
        if (!lastPenaltyStep.penaltyCost || parseFloat(lastPenaltyStep.penaltyCost) <= 0) {
          lastPenaltyStep.penaltyCost = bfPenaltyStep && bfPenaltyStep.penaltyCost ? parseFloat(bfPenaltyStep.penaltyCost) : 0;
        }
        lastPenaltyCost = parseFloat(lastPenaltyStep.penaltyCost);
        this.penalty.penaltyStep.push({
          minDays: 1,
          maxDays: null,
          moreDays: bfPenaltyStep ? parseFloat(bfPenaltyStep.maxDays) + 2 : 0,
          penaltyCost: lastPenaltyCost || 0,
          isPerDay: true
        });
      } else {
        this.penalty.penaltyStep = [
          {
            minDays: 1,
            maxDays: null,
            moreDays: 0,
            penaltyCost: 0,
            isPerDay: true
          },
          {
            minDays: 1,
            maxDays: null,
            moreDays: 0,
            penaltyCost: 0,
            isPerDay: true
          }
        ];
      }
    },
    delPenaltyStep(index = -1) {
      let indexSplce = index > -1 ? index : this.penalty.penaltyStep.length - 1;
      if (this.penalty) {
        if (this.penalty.penaltyStep && this.penalty.penaltyStep.length > 2) {
          this.penalty.penaltyStep.splice(indexSplce, 1);
        }
      }
    },
    checkInputStep(index) {
      if (index >= 0 && this.penalty?.penaltyStep?.length >= 2) {
        this.updatePenaltyStep(this.penalty.penaltyStep, index);
      }
    },
    updatePenaltyStep(penaltyStep, index) {
      for (const idx in penaltyStep) {
        let bfPenalty = penaltyStep[idx - 1];
        let penalty = penaltyStep[idx];
        if (idx > index && penalty && bfPenalty) {
          penalty.minDays = parseFloat(bfPenalty.maxDays ? bfPenalty.maxDays : 0) + 1;
          if (parseFloat(penalty.maxDays) < parseFloat(penalty.minDays)) {
            penalty.maxDays = penalty.minDays;
          }
          penalty.moreDays = penalty.minDays;
        }
      }
    },
    toCurrencySuffix(value) {
      return this.$helper.toCurrency(value, this.tabType, { isSuffixText: true, isThEn: true });
    },
    checkRowStep(penaltyStep, isLast = false) {
      let res = {
        isError: false,
        isWarning: false,
        textError: [],
        textRes: "",
        textMinCost: "",
        textMaxCost: ""
      };
      
      if (isLast && parseFloat(penaltyStep.penaltyCost) > 0) {
        this.setLastStepTexts(res, penaltyStep);
      } else if (
        parseFloat(penaltyStep.penaltyCost) > 0 &&
        parseFloat(penaltyStep.maxDays) >= parseFloat(penaltyStep.minDays)
      ) {
        this.setNormalStepTexts(res, penaltyStep);
      } else {
        this.setErrorTexts(res, penaltyStep, 'days', isLast);
      }
      return res;
    },
    setLastStepTexts(res, penaltyStep, type = 'days') {
      if (type === 'days') {
        res.textRes = `
          ${this.$t('penalty.payment_late_more_than')} ${penaltyStep.moreDays} ${this.$t('penalty.more_days')} ${this.$t('penalty.calculate_penalty')}
          ${penaltyStep.isPerDay ? this.$t('penalty.penalty_textres_per_day') : this.$t('penalty.penalty_textres_flat_rate')} ${this.toCurrencySuffix(parseFloat(penaltyStep.penaltyCost))}
        `;
        
        res.textMinCost = `
          ${this.$t('penalty.minimum_penalty')} 
          ${this.toCurrencySuffix(parseFloat(penaltyStep.penaltyCost) * (penaltyStep.isPerDay ? penaltyStep.minDays : 1))}
        `;
        res.textMaxCost = `
          ${this.$t('penalty.maximum_penalty')} 
          ${penaltyStep.isPerDay ? this.$t('penalty.penalty_maxcost_unlimited') : this.toCurrencySuffix(parseFloat(penaltyStep.penaltyCost))}
        `;
      } else {
        res.textRes = `
          ${this.$t('penalty.overdue_since')} ${penaltyStep.more - 1} ${this.$helper.currencyThEn(this.tabType)} ${this.$t('validate.up_to')} ${this.$t('penalty.calculate_penalty')}
          ${penaltyStep.isPerDay ? this.$t('penalty.penalty_textres_per_day') : this.$t('penalty.penalty_textres_flat_rate')} ${this.toCurrencySuffix(parseFloat(penaltyStep.penaltyCost))}
        `;
        
        if (penaltyStep.isPerDay) {
        res.textMinCost = `
          ${this.$t('penalty.minimum_penalty')} 
            ${this.toCurrencySuffix(parseFloat(penaltyStep.penaltyCost), this.tabType)}
          `;
        } else {
          res.textMinCost = `
            ${this.$t('penalty.minimum_penalty')} 
            ${this.toCurrencySuffix(parseFloat(penaltyStep.penaltyCost) * (penaltyStep.isPerDay ? penaltyStep.min : 1), this.tabType)}
          `;
        }
        res.textMaxCost = `${this.$t('penalty.maximum_penalty')} ${this.$t('penalty.penalty_maxcost_unlimited')}`;
      }
    },
    setNormalStepTexts(res, penaltyStep, type = 'days') {
      if (type === 'days') {
        res.textRes = `
            ${this.$t('penalty.payment_late_more_than')} ${penaltyStep.minDays} - ${penaltyStep.maxDays} ${this.$t('day')} ${this.$t('penalty.calculate_penalty')}
            ${penaltyStep.isPerDay ? this.$t('penalty.penalty_textres_per_day') : this.$t('penalty.penalty_textres_flat_rate')} ${this.toCurrencySuffix(parseFloat(penaltyStep.penaltyCost))}
          `;
          
        res.textMinCost = `
            ${this.$t('penalty.minimum_penalty')} 
            ${this.toCurrencySuffix(parseFloat(penaltyStep.penaltyCost) * (penaltyStep.isPerDay ? penaltyStep.minDays : 1))}
          `;
          
        res.textMaxCost = `
          ${this.$t('penalty.maximum_penalty')} 
          ${this.toCurrencySuffix((penaltyStep.penaltyCost) * (penaltyStep.isPerDay ? penaltyStep.maxDays : 1))}
        `;
      } else {
        res.textRes = `
            ${this.$t('penalty.overdue_since')} ${penaltyStep.min - 1} - ${penaltyStep.max} ${this.$helper.currencyThEn()} ${this.$t('penalty.calculate_penalty')}
            ${penaltyStep.isPerDay ? this.$t('penalty.penalty_textres_per_day') : this.$t('penalty.penalty_textres_flat_rate')} ${this.toCurrencySuffix(parseFloat(penaltyStep.penaltyCost))}
          `;
        if (penaltyStep.isPerDay) {
        res.textMinCost = `
            ${this.$t('penalty.minimum_penalty')} 
            ${this.toCurrencySuffix(parseFloat(penaltyStep.penaltyCost), this.tabType)}
          `;
        } else {
          res.textMinCost = `
            ${this.$t('penalty.minimum_penalty')} 
            ${this.toCurrencySuffix(parseFloat(penaltyStep.penaltyCost) * (penaltyStep.isPerDay ? penaltyStep.min : 1), this.tabType)}
          `;
        }
        res.textMaxCost = `${this.$t('penalty.maximum_penalty')} ${this.$t('penalty.penalty_maxcost_unlimited')}`;
      }
    },
    setErrorTexts(res, penaltyStep, type = 'days', isLast = false) {
      if (!isLast) {
        if (type === 'days') {
          if (!penaltyStep.maxDays || parseFloat(penaltyStep.maxDays) < parseFloat(penaltyStep.minDays)) {
            res.isError = true;
            res.textError.push(`${this.$t('validate.please_input_due_date_more_or_equal_days')}`);
          }
        } else if (type === 'unpaid') {
          if (!penaltyStep.max || parseFloat(penaltyStep.max) < parseFloat(penaltyStep.min)) {
            res.isError = true;
            res.textError.push(`${this.$t('validate.please_input_max_penalty_cost_more_or_equal_to_min_penalty_cost')}`);
          }
        }
      }

      if (parseFloat(penaltyStep.penaltyCost) < 0 || isNaN(parseFloat(penaltyStep.penaltyCost))) {
        res.isError = true;
        res.textError.push(`${this.$t('validate.please_input_min_penalty_cost_since')} 0 ${this.$helper.currencyThEn(this.tabType)}${this.$t('validate.up_to')}`);
      } else if (parseFloat(penaltyStep.penaltyCost) === 0) {
        res.isWarning = true;
        res.textError.push(`${this.$t('penalty.penalty_cost_is_equal_to')} 0 ${this.$helper.currencyThEn(this.tabType)}`);
      }
    },

    addPenaltyStepUnpaid() {
      if (this.penalty && this.penalty.penaltyStepUnpaid && this.penalty.penaltyStepUnpaid.length >= 2) {
        let penaltyStepLength = this.penalty.penaltyStepUnpaid.length;
        let bfPenaltyStep = this.penalty.penaltyStepUnpaid[penaltyStepLength - 2];
        let lastPenaltyStep = this.penalty.penaltyStepUnpaid[penaltyStepLength - 1];
        let lastPenaltyCost = 0;
        lastPenaltyStep.min = bfPenaltyStep ? parseFloat(bfPenaltyStep.max) + 1 : 1;
        lastPenaltyStep.max = bfPenaltyStep ? parseFloat(bfPenaltyStep.max) + 1 : 0;
        if (!lastPenaltyStep.penaltyCost || parseFloat(lastPenaltyStep.penaltyCost) <= 0) {
          lastPenaltyStep.penaltyCost = bfPenaltyStep && bfPenaltyStep.penaltyCost ? parseFloat(bfPenaltyStep.penaltyCost) : 0;
        }
        lastPenaltyCost = parseFloat(lastPenaltyStep.penaltyCost);
        this.penalty.penaltyStepUnpaid.push({
          min: 1,
          max: null,
          more: bfPenaltyStep ? parseFloat(bfPenaltyStep.max) + 2 : 0,
          penaltyCost: lastPenaltyCost || 0,
          isPerDay: true
        });
      } else {
        this.penalty.penaltyStepUnpaid = [
          {
            min: 1,
            max: null,
            more: 0,
            penaltyCost: 0,
            isPerDay: true
          },
          {
            min: 1,
            max: null,
            more: 0,
            penaltyCost: 0,
            isPerDay: true
          }
        ];
      }
    },
    delPenaltyStepUnpaid(index = -1) {
      let indexSplce = index > -1 ? index : this.penalty.penaltyStepUnpaid.length - 1;
      if (this.penalty) {
        if (this.penalty.penaltyStepUnpaid && this.penalty.penaltyStepUnpaid.length > 2) {
          this.penalty.penaltyStepUnpaid.splice(indexSplce, 1);
        }
      }
    },
    checkInputStepUnpaid(index) {
      if (index >= 0 && this.penalty?.penaltyStepUnpaid?.length >= 2) {
        this.updatePenaltyStepUnpaid(this.penalty.penaltyStepUnpaid, index);
      }
    },
    updatePenaltyStepUnpaid(penaltyStep, index) {
      for (const idx in penaltyStep) {
        let bfPenalty = penaltyStep[idx - 1];
        let penalty = penaltyStep[idx];
        if (idx > index && penalty && bfPenalty) {
          penalty.min = parseFloat(bfPenalty.max ? bfPenalty.max : 0) + 1;
          if (parseFloat(penalty.max) < parseFloat(penalty.min)) {
            penalty.max = penalty.min;
          }
          penalty.more = penalty.min;
        }
      }
    },
    checkRowStepUnpaid(penaltyStep, isLast = false) {
      let res = {
        isError: false,
        textError: [],
        textRes: "",
        textMinCost: "",
        textMaxCost: ""
      };

      if (isLast && parseFloat(penaltyStep.penaltyCost) > 0) {
        this.setLastStepTexts(res, penaltyStep, 'unpaid');
      } else if (
        parseFloat(penaltyStep.penaltyCost) > 0 &&
        parseFloat(penaltyStep.max) >= parseFloat(penaltyStep.min)
      ) {
        this.setNormalStepTexts(res, penaltyStep, 'unpaid');
      } else {
        this.setErrorTexts(res, penaltyStep, 'unpaid', isLast);
      }
      return res;
    },
  }
};
</script>

<style lang="scss" scoped>
::-webkit-scrollbar {
  height: 12px; /* height of horizontal scrollbar ← You're missing this */
  width: 4px; /* width of vertical scrollbar */
  border: 1px solid #e9e9e9;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  background: #e9e9e9;
}
::-webkit-scrollbar-thumb:horizontal {
  background: #c7c7c7;
  border-radius: 10px;
}
.v-card-text {
  min-height: calc(
    100vh - 96px - 60px - 64px - 64px - 24px - 20px - 8px - 10px
  );
}
.sticky-bottom {
  background: rgba(255, 255, 255, 0.74);
  position: sticky;
  bottom: 0;
  justify-content: center;
}
.due-date-box {
  div {
    align-self: baseline;
    padding: 0 10px;
    font-size: 15px;
  }
  div.text {
    font-size: 18px;
  }
  div.text-p {
    font-size: 16px;
  }
  div.days {
    max-width: 200px;
  }
  div.penalty-step-box {
    padding: 0;
    padding-right: 15px;
    margin-bottom: 15px;
    border: 1px solid rgba(128, 128, 128, 0.2);
    border-radius: 5px;
    width: 100%;
    max-width: 1100px;
    overflow-x: auto;
    .left-box{
      min-width: 500px;
      width: 500px;
    }
  }
}
</style>

<style>
.v-messages__message {
  line-height: 1.5 !important;
}
</style>